// components/schedule/ScheduleDialog/useScheduleMasterData.ts

import { useState, useEffect, useCallback, useRef } from 'react';
import { Member } from '../../../types/member';
import { Vendor, VendorMember } from '../../../types/vendor';
import { Car } from '../../../types/car';
import { Customer } from '../../../types/otherSchedule';
import { memberService } from '../../../services/member';
import { vendorService } from '../../../services/vendor';
import { carService } from '../../../services/car';
import { scheduleService } from '../../../services/schedule';
import { otherScheduleService } from '../../../services/otherSchedule';
// カレンダーサービスをインポート
import { calendarService } from '../../../services/calendar';

// 稼働日情報の型定義
interface WorkingDayInfo {
  date: string;
  isWorkingDay: boolean;
}

// APIレスポンスの型
interface CalendarServiceResponse {
  success: boolean;
  workingDays: string[]; // サーバーから返ってくるのは稼働日リスト（文字列配列）のみと想定
}

export const useScheduleMasterData = (
  open: boolean,
  scheduleType: 'construction' | 'other'
) => {
  // マスターデータ
  const [members, setMembers] = useState<Member[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [vendorMembers, setVendorMembers] = useState<VendorMember[]>([]);
  const [cars, setCars] = useState<Car[]>([]);
  const [constructions, setConstructions] = useState<any[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  
  // 読み込み状態
  const [loading, setLoading] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  
  // 検索関連
  const [searchQuery, setSearchQuery] = useState('');
  
  // 拡張状態
  const [expandedVendors, setExpandedVendors] = useState<number[]>([]);

  // 稼働日カレンダー情報を保持する状態
  const [workingDays, setWorkingDays] = useState<WorkingDayInfo[]>([]);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  
  // 稼働日取得のキャッシュと状態管理用のRef
  const workingDaysCache = useRef<Map<string, boolean>>(new Map());
  const pendingDateRanges = useRef<Array<{start: string, end: string}>>([]);
  const isFetchingWorkingDays = useRef<boolean>(false);

  // マスターデータの取得
  const fetchMasterData = useCallback(async () => {
    setLoading(true);
    try {
      const [membersData, vendorsData, vendorMembersData, carsData] = await Promise.all([
        memberService.getAllMembers(),
        vendorService.getAllVendors(),
        vendorService.getAllVendorMembers(),
        carService.getAllCars()
      ]);
      setMembers(membersData);
      setVendors(vendorsData);
      setVendorMembers(vendorMembersData);
      setCars(carsData);
    } catch (error) {
      console.error('Error fetching master data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // 日付をYYYY-MM-DD形式に変換するヘルパー関数
  const formatDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 指定した期間の稼働日情報を取得 (無限ループの原因を修正)
  const fetchWorkingDays = useCallback(async (startDate: string, endDate: string) => {
    // すでに取得中の場合は後で処理するためにキューに追加
    if (isFetchingWorkingDays.current) {
      pendingDateRanges.current.push({ start: startDate, end: endDate });
      return;
    }
    
    // すべての日付がキャッシュにある場合はスキップ
    let allDatesInCache = true;
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = formatDateString(d);
      if (!workingDaysCache.current.has(dateStr)) {
        allDatesInCache = false;
        break;
      }
    }
    
    if (allDatesInCache) {
      return;
    }
    
    // 稼働日情報の取得
    isFetchingWorkingDays.current = true;
    setLoadingCalendar(true);
    
    try {
      const response = await calendarService.getWorkingDaysInRange(startDate, endDate) as CalendarServiceResponse;
      
      if (response.success && Array.isArray(response.workingDays)) {
        // 開始日から終了日までの日付配列を生成
        const allDates: WorkingDayInfo[] = [];
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const dateStr = formatDateString(d);
          const isWorkDay = response.workingDays.includes(dateStr);
          
          // キャッシュに追加
          workingDaysCache.current.set(dateStr, isWorkDay);
          
          // 返却データ用の配列に追加
          allDates.push({
            date: dateStr,
            isWorkingDay: isWorkDay
          });
        }
        
        // 既存のデータと新しいデータをマージ
        setWorkingDays(prevDays => {
          const existingDatesMap = new Map(prevDays.map(day => [day.date, day]));
          
          // 新しいデータで上書き
          allDates.forEach(day => {
            existingDatesMap.set(day.date, day);
          });
          
          return Array.from(existingDatesMap.values());
        });
      }
    } catch (error) {
      console.error('Error fetching working days:', error);
    } finally {
      setLoadingCalendar(false);
      isFetchingWorkingDays.current = false;
      
      // 待機中のリクエストがあれば次を処理
      if (pendingDateRanges.current.length > 0) {
        const nextRange = pendingDateRanges.current.shift();
        if (nextRange) {
          fetchWorkingDays(nextRange.start, nextRange.end);
        }
      }
    }
  }, []);

  // 日付が稼働日かどうかを判定するヘルパー関数 (キャッシュを優先使用)
  const isWorkingDay = useCallback((date: Date): boolean => {
    const dateStr = formatDateString(date);
    
    // キャッシュを確認
    if (workingDaysCache.current.has(dateStr)) {
      return workingDaysCache.current.get(dateStr) ?? false;
    }
    
    // 配列データを確認
    const foundDay = workingDays.find(day => day.date === dateStr);
    if (foundDay) {
      return foundDay.isWorkingDay;
    }
    
    // デフォルト値として平日（土日以外）を稼働日とする
    const dayOfWeek = date.getDay();
    return dayOfWeek !== 0 && dayOfWeek !== 6; // 0:日曜、6:土曜
  }, [workingDays]);

  // 未完了工事のみを取得
  const fetchIncompleteConstructions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await scheduleService.getIncompleteConstructions();
      setConstructions(data);
    } catch (error) {
      console.error('Error fetching incomplete constructions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // 顧客一覧を取得
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await otherScheduleService.getCustomers();
      setCustomers(data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // 工事に紐づく担当者を取得
  const fetchConstructionMembers = useCallback(async (constructionId: number) => {
    if (constructionId === 0) return [];

    setLoadingMembers(true);
    try {
      const members = await scheduleService.getConstructionMembers(constructionId);
      setLoadingMembers(false);
      return members || [];
    } catch (error) {
      console.error('Error fetching construction members:', error);
      setLoadingMembers(false);
      return [];
    }
  }, []);

  // 検索クエリ変更ハンドラ
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  // ベンダー展開状態の切り替え
  const handleVendorExpand = useCallback((vendorId: number, isExpanded: boolean) => {
    setExpandedVendors(prev => 
      isExpanded 
        ? [...prev, vendorId] 
        : prev.filter(id => id !== vendorId)
    );
  }, []);

  // フィルター適用したメンバーリスト
  const filteredMembers = members.filter(member => {
    const searchText = `${member.LastName}${member.FirstName}${member.staff_initial || ''}`.toLowerCase();
    return searchText.includes(searchQuery.toLowerCase());
  });

  // フィルター適用した外注メンバーリスト
  const filteredVendorMembers = vendorMembers.filter(member => {
    const searchText = member.MemberName.toLowerCase();
    return searchText.includes(searchQuery.toLowerCase());
  });

  // ダイアログが開かれた時にマスターデータを読み込む
  useEffect(() => {
    if (open) {
      fetchMasterData();

      // 予定タイプに基づいて必要なデータを取得
      if (scheduleType === 'construction') {
        fetchIncompleteConstructions();
      } else {
        fetchCustomers();
      }
    }
    
    // クリーンアップ関数
    return () => {
      // コンポーネントのアンマウント時にキャッシュをクリア
      pendingDateRanges.current = [];
    };
  }, [open, scheduleType, fetchMasterData, fetchIncompleteConstructions, fetchCustomers]);

  return {
    loading,
    loadingMembers,
    loadingCalendar,
    members,
    vendors,
    vendorMembers,
    cars,
    constructions,
    customers,
    expandedVendors,
    searchQuery,
    filteredMembers,
    filteredVendorMembers,
    workingDays,
    isWorkingDay,
    fetchWorkingDays,
    fetchConstructionMembers,
    handleSearchChange,
    handleVendorExpand,
    setExpandedVendors
  };
};

export default useScheduleMasterData;